/* eslint-disable react/prop-types */
import React, { useCallback, useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef, type MRT_Cell } from 'material-react-table';
import { SectionFieldDTO } from '../magic-report.type';
import type { DynamicTableColumnDef, DynamicTableRow } from '../magic-report.type';
import { usePrompt } from '../../../AppPrompt';
import DynamicDatePicker from '../../../common/DynamicDatePicker';
import { formatCurrency } from '../../../../common/utils/common.util';

const TABLE_ROWS_FIELD_NAME = 'dynamicTableRows';

const parseRow = (value?: string): DynamicTableRow | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed._id === 'string' ? (parsed as DynamicTableRow) : null;
  } catch {
    return null;
  }
};

const serializeRow = (row: DynamicTableRow): string => JSON.stringify(row);

const sharedHeadCellSx = (theme: any) => ({
  backgroundColor: theme.palette.primary.main,
  position: 'sticky' as const,
  top: 0,
  color: '#ffffff',
  padding: '8px 9px',
  border: 'none',
  justifyContent: 'flex-start',
  textAlign: 'left',
  fontSize: '0.9rem',
  fontWeight: 'normal',
  whiteSpace: 'normal' as const,
  wordWrap: 'break-word' as const,
  '& .Mui-TableHeadCell-Content-Wrapper': {
    justifyContent: 'flex-start',
    textAlign: 'left',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  '&[data-pinned="true"]:not([role="columnheader"])::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.primary.main,
    zIndex: 0,
  },
  '&[data-pinned="true"]:not([role="columnheader"])': {
    zIndex: 1,
  },
});

const sharedBodyCellSx = (rowIndex: number) => ({
  backgroundColor: rowIndex % 2 === 0 ? '#f9f9f9' : '#ffffff',
  border: 'none',
  fontSize: '0.875rem',
  '&[data-pinned="true"]::before': {
    backgroundColor: rowIndex % 2 === 0 ? '#f9f9f9' : '#ffffff',
  },
});

const sharedFooterCellSx = (theme: any) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  fontWeight: 'normal',
  fontSize: '0.9rem',
  padding: '8px 9px',
  border: 'none',
  '&[data-pinned="true"]::before': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.primary.main,
    zIndex: 0,
  },
  '&[data-pinned="true"]': {
    zIndex: 1,
  },
});

export interface DynamicSectionTableProps {
  columns: DynamicTableColumnDef[];
  rowsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
}

export const DynamicSectionTable: React.FC<DynamicSectionTableProps> = ({ columns, rowsField, handleFieldChange }) => {
  const { getConfirmation } = usePrompt();

  const rows: DynamicTableRow[] = useMemo(
    () => rowsField?.entries?.map(e => parseRow(e.value)).filter((r): r is DynamicTableRow => r !== null) || [],
    [rowsField?.entries],
  );

  const saveRows = useCallback(
    (updatedRows: DynamicTableRow[]) => {
      const entries = updatedRows.map((row, order) => ({
        order,
        value: serializeRow(row),
      }));
      handleFieldChange(TABLE_ROWS_FIELD_NAME, { entries });
    },
    [handleFieldChange],
  );

  const handleSaveCell = useCallback(
    (cell: MRT_Cell<DynamicTableRow>, value: unknown) => {
      const row = cell.row.original;
      const columnId = cell.column.id as string;
      const colDef = columns.find(c => c.id === columnId);
      let parsed: string | number = value as string | number;
      if (colDef?.type === 'numeric' || colDef?.type === 'currency') {
        const num = typeof value === 'string' ? parseFloat(value) : Number(value);
        parsed = Number.isNaN(num) ? 0 : num;
      } else {
        parsed = value == null ? '' : String(value);
      }
      const updatedRow: DynamicTableRow = { ...row, [columnId]: parsed };
      const updatedRows = rows.map(r => (r._id === row._id ? updatedRow : r));
      saveRows(updatedRows);
    },
    [columns, rows, saveRows],
  );

  const handleAddRow = useCallback(() => {
    const newRow: DynamicTableRow = {
      _id: `row-${Date.now()}`,
      ...columns.reduce<Record<string, string | number>>((acc, col) => {
        if (col.type === 'numeric' || col.type === 'currency') {
          acc[col.id] = 0;
        } else {
          acc[col.id] = '';
        }
        return acc;
      }, {}),
    };
    saveRows([...rows, newRow]);
  }, [columns, rows, saveRows]);

  const handleDeleteRow = useCallback(
    async (rowId: string) => {
      const isConfirmed = await getConfirmation({
        heading: 'Delete row',
        description: 'Are you sure you want to delete this row?',
        confirmButton: { label: 'Delete', color: 'error' },
        cancelButton: { label: 'Cancel', color: 'secondary' },
      });
      if (!isConfirmed) return;
      saveRows(rows.filter(r => r._id !== rowId));
    },
    [getConfirmation, rows, saveRows],
  );

  const mrtColumns = useMemo<MRT_ColumnDef<DynamicTableRow>[]>(
    () => [
      ...columns.map((col): MRT_ColumnDef<DynamicTableRow> => {
        // Date column: use DynamicDatePicker for editing
        if (col.type === 'date') {
          return {
            accessorKey: col.id,
            header: col.header,
            size: 170,
            Cell: ({ cell }) => {
              const row = cell.row.original;
              const value = (row[col.id] as string | undefined) ?? null;
              return (
                <DynamicDatePicker
                  fieldName={col.id}
                  value={value}
                  onChange={(_, newValue) => {
                    // newValue is string | null (yyyy-MM-dd)
                    handleSaveCell(cell, newValue ?? '');
                  }}
                />
              );
            },
            enableEditing: false,
            Footer: () => '',
            muiTableHeadCellProps: {
              sx: sharedHeadCellSx,
            },
            muiTableBodyCellProps: ({ row }) => ({
              sx: sharedBodyCellSx(row.index),
            }),
            muiTableFooterCellProps: {
              sx: sharedFooterCellSx,
            },
          };
        }

        const isNumeric = col.type === 'numeric' || col.type === 'currency';

        return {
          accessorKey: col.id,
          header: col.header,
          size: 150,
          Cell:
            col.type === 'currency'
              ? ({ cell }) => {
                  const raw = cell.getValue<number | string | null>() ?? 0;
                  const numeric = typeof raw === 'number' ? raw : parseFloat(String(raw) || '0') || 0;
                  return formatCurrency(numeric.toString(), true);
                }
              : undefined,
          muiEditTextFieldProps: ({ cell }) =>
            isNumeric
              ? {
                  type: 'number',
                  sx: { '& .MuiInputBase-input': { textAlign: 'right' } },
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    handleSaveCell(cell, e.target.value);
                  },
                }
              : {
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    handleSaveCell(cell, e.target.value);
                  },
                },
          Footer: () => '',
          muiTableHeadCellProps: {
            sx: sharedHeadCellSx,
          },
          muiTableBodyCellProps: ({ row }) => ({
            sx: sharedBodyCellSx(row.index),
          }),
          muiTableFooterCellProps: {
            sx: sharedFooterCellSx,
          },
        };
      }),
      {
        id: 'actions',
        header: '',
        size: 60,
        enableEditing: false,
        enableResizing: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <IconButton
              size='small'
              onClick={() => handleDeleteRow(row.original._id)}
              sx={{ color: 'grey.600', '&:hover': { color: 'error.main' } }}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Box>
        ),
        Footer: () => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <IconButton size='small' onClick={handleAddRow} color='primary'>
              <AddIcon fontSize='small' />
            </IconButton>
          </Box>
        ),
        muiTableHeadCellProps: {
          align: 'right' as const,
          sx: (theme: any) => ({
            ...sharedHeadCellSx(theme),
            justifyContent: 'flex-end',
            '& .Mui-TableHeadCell-Content-Wrapper': { justifyContent: 'flex-end' },
          }),
        },
        muiTableBodyCellProps: {
          align: 'right' as const,
          sx: { border: 'none', fontSize: '0.875rem' },
        },
        muiTableFooterCellProps: {
          align: 'right' as const,
          sx: (theme: any) => ({ ...sharedFooterCellSx(theme), justifyContent: 'flex-end' }),
        },
      },
    ],
    [columns, handleSaveCell, handleDeleteRow, handleAddRow],
  );

  const table = useMaterialReactTable({
    columns: mrtColumns,
    data: rows,
    getRowId: row => row._id,
    initialState: {
      density: 'compact',
      showGlobalFilter: false,
    },
    layoutMode: 'grid',
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnResizing: true,
    enablePagination: false,
    enableDensityToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableSorting: false,
    enableStickyFooter: true,
    editDisplayMode: 'cell',
    enableEditing: true,
    muiTableProps: {
      sx: {
        marginTop: 0,
        borderCollapse: 'collapse',
        '& .MuiTableCell-root': {
          borderBottom: 'none',
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: 'none',
        backgroundColor: 'transparent',
        margin: 0,
        padding: 0,
        width: '100%',
        display: 'contents',
      },
    },
    muiTableContainerProps: {
      sx: {
        padding: 0,
        margin: 0,
        maxHeight: '400px',
        overflow: 'auto',
        maxWidth: '100%',
        width: '100%',
      },
    },
    muiTableHeadProps: { sx: { position: 'sticky', top: 0, zIndex: 2, display: 'contents' } },
    muiTableHeadCellProps: {
      sx: (theme: any) => ({
        backgroundColor: `${theme.palette.primary.main} !important`,
        color: '#ffffff',
        padding: '8px 9px',
        border: 'none !important',
        borderBottom: 'none !important',
        borderRight: 'none !important',
        fontSize: '0.9rem',
      }),
    },
    muiTableFooterCellProps: {
      sx: (theme: any) => ({
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
        padding: '8px 9px',
        border: 'none',
        fontSize: '0.9rem',
      }),
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: `${row.index % 2 === 0 ? '#f9f9f9' : '#ffffff'} !important`,
        border: 'none',
      },
    }),
    muiTableBodyCellProps: { sx: { border: 'none', fontSize: '0.875rem' } },
  });

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <IconButton size='small' onClick={handleAddRow} color='primary' sx={{ mr: 0.5 }}>
          <AddIcon fontSize='small' />
        </IconButton>
      </Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          overflow: 'hidden',
          '& .MuiPaper-root': {
            margin: 0,
            padding: 0,
          },
          '& .MuiTableContainer-root': {
            margin: 0,
          },
        }}
      >
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  );
};
